import camelcaseKeys from 'camelcase-keys';
import { z } from 'zod';

import type { PlaceId } from '@brightlocal/shared';

import { PaginationSchema } from '../../schemas';

const KeywordSuggestionSchema = z
  .object({
    keyword: z.string(),
    score: z.number(),
    branded: z.boolean(),
    has_geo_modifier: z.boolean(),
    keyword_style: z.string().optional(),
    source: z.string()
  })
  .transform((data) => camelcaseKeys(data));

export const KeywordSuggestionsResponseSchema = z.object({
  business: z.object({
    name: z.string(),
    categories: z.array(z.string()),
    location: z.string()
  }),
  data: z.array(KeywordSuggestionSchema),
  pagination: PaginationSchema,
  meta: z
    .object({
      from_cache: z.number(),
      from_generation: z.number()
    })
    .transform((data) => camelcaseKeys(data))
});

export type KeywordSuggestionsResponse = z.infer<typeof KeywordSuggestionsResponseSchema>;

/** Style of the generated keywords, per the Suggestion Service contract. */
export const KEYWORD_STYLE = {
  STANDARD: 'standard',
  LONG_TAIL: 'long_tail',
  QUESTION: 'question',
  PROMPT: 'prompt',
  COMMERCIAL: 'commercial',
  COMPARISON: 'comparison',
  SEASONAL: 'seasonal',
  MIXED: 'mixed'
} as const;

/** Union of `KEYWORD_STYLE` values accepted by the Suggestion Service. */
export type KeywordStyle = (typeof KEYWORD_STYLE)[keyof typeof KEYWORD_STYLE];

/**
 * Where the returned keywords come from, per the Suggestion Service contract.
 * `all` serves the cache first and generates the shortfall, `cache` never generates, and
 * `generate` always produces new keywords via OpenAI.
 */
export type KeywordSource = 'all' | 'cache' | 'generate';

/** Parameters for `POST /data/v1/suggestions/keywords`. */
export interface KeywordSuggestionsParams {
  /** Google Place ID of the business to generate suggestions for. */
  placeId: PlaceId;
  /** Number of suggestions to return (API accepts 1-100). */
  numPerPage: number;
  /** Keyword style to generate; omitting it leaves the style unfiltered. */
  keywordStyle?: KeywordStyle;
  /** Include keywords containing the business name; the API defaults to `false` when omitted. */
  allowBranded?: boolean;
  /** Where keywords come from; the API defaults to `all` when omitted. */
  source?: KeywordSource;
}
